import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useDebounce } from "@reactuses/core";
import { useTranslation } from "react-i18next";
import { useDateFormatter } from "../../../../i18n/useDateFormatter";
import { PageContainer } from "../../../../components/core/basic/PageContainer";
import BreadcrumbDates from "../../../../components/core/blocks/BreadcrumbDates";
import MetricsCard from "../../../../components/custom/merchandise/MetricsCard";
import { usePersistedTableState } from "../../../../components/table/usePersistedTableState";
import { idsToVisibilityState } from "../../../../components/table/utils";
import { DatesProvider, useDates } from "../../../../dates/DatesContext";
import useQueryParams from "../../../../hooks/useQueryParams";
import { useModuleEnabled } from "../../../../redux/selectors/modules";
import { useAcosOrRoas } from "../../../../utils/acosOrRoas";
import { ORG_SUMMARY_REPORT_BREADCRUMB_SPEC, REPORT_LINKS } from "../../../../utils/dashboardLinks";
import getMetricSpec from "../../../../modules/merchandising/utils/metrics";
import { useStandardEntityMetrics } from "../../../../modules/merchandising/components/tables/metricUtils";
import {
  useGetSponsoredAdsAudienceByDate,
  useGetSponsoredAdsAudienceTotals,
} from "../../../../modules/perf_reports/api/audience/useSponsoredAdsAudienceDatasetsQuery";
import { useSponsoredAdsAudienceReportColumns } from "../../../../modules/perf_reports/audience_report/useSponsoredAdsAudienceReportColumns";
import { SponsoredAdsAudienceReportTable } from "../../../../modules/perf_reports/audience_report/SponsoredAdsAudienceReportTable";
import {
  sponsoredAdsAudienceAdTypeSchema,
  sponsoredAdsAudienceGroupingSchema,
  type SponsoredAdsAudienceAdType,
  type SponsoredAdsAudienceGrouping,
} from "../../../../modules/perf_reports/audience_report/sponsoredAdsAudienceGroupings";

const SB_EXCLUDED_METRIC_IDS = new Set(["orders", "conversion_rate"]);

function SponsoredAdsAudienceReportBody({
  grouping,
  onGroupingChange,
  adType,
  setAdType,
}: {
  grouping: SponsoredAdsAudienceGrouping;
  onGroupingChange: (grouping: string) => void;
  adType: SponsoredAdsAudienceAdType;
  setAdType: (adType: SponsoredAdsAudienceAdType) => void;
}) {
  const { t } = useTranslation(["perf_reports"]);
  const acosOrRoas = useAcosOrRoas();
  const standardMetricIds = useStandardEntityMetrics("spAudience");
  const metricIds = useMemo(
    () =>
      adType === "sponsoredBrand" ?
        standardMetricIds.filter((id) => !SB_EXCLUDED_METRIC_IDS.has(id))
      : standardMetricIds,
    [standardMetricIds, adType],
  );
  const columns = useSponsoredAdsAudienceReportColumns(grouping, metricIds);
  const { start, end, group } = useDates();
  const longDate = useDateFormatter("long");

  const defaultVisible =
    grouping === "segment_name,campaign_id" ?
      ["segment_name", "campaign_name", ...metricIds]
    : ["segment_name", ...metricIds];

  const defaultHiddenCols = idsToVisibilityState(
    defaultVisible,
    columns.filter((column) => Boolean(column.id)).map((column) => column.id!),
  );

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: `${adType}AudienceReport-${grouping}`,
    initialState: {
      columnVisibility: {
        ...defaultHiddenCols,
        acos: acosOrRoas(true, false),
        roas: acosOrRoas(false, true),
      },
      columnOrder: defaultVisible,
      columnPinning: {
        start: ["segment_name"],
        end: [],
      },
      sorting: [{ id: "ad_sales", desc: true }],
    },
  });

  const debounceSearch = useDebounce(state.globalFilter, 500);
  const filters = useMemo(() => [...state.columnFilters], [state.columnFilters]);

  const byDateGetter = useGetSponsoredAdsAudienceByDate(adType);
  const byDateQuery = useQuery(byDateGetter({ filters, search: debounceSearch }));
  const totalsGetter = useGetSponsoredAdsAudienceTotals(adType);
  const totalsQuery = useQuery(totalsGetter({ filters, search: debounceSearch }));

  const timelineData = useMemo(() => byDateQuery.data?.data ?? [], [byDateQuery.data?.data]);

  const metrics = useMemo(() => metricIds.map((id) => getMetricSpec(id)), [metricIds]);

  return (
    <PageContainer withBreadcrumb>
      <MetricsCard
        id={`${adType}AudienceReport`}
        title={t(($) => $.perf_reports["{adType} Audience Performance"], {
          adType:
            adType === "sponsoredBrand" ?
              t(($) => $.perf_reports["Sponsored Brand"])
            : t(($) => $.perf_reports["Sponsored Product"]),
        })}
        totals={{
          data: totalsQuery.data?.data,
          loading: totalsQuery.isFetching && (!totalsQuery.data || totalsQuery.isPlaceholderData),
        }}
        timeline={{
          data: timelineData,
          loading: byDateQuery.isFetching && (!byDateQuery.data || byDateQuery.isPlaceholderData),
        }}
        metrics={metrics}
        showMetricsSelector
        initialSelectedMetric={["ad_sales", "ad_spend", acosOrRoas("acos", "roas")]}
        group={group}
        showAnnotations={true}
        byline={
          <>
            {longDate.format(start.toDate())}
            {" - "}
            {longDate.format(end.toDate())}
          </>
        }
        context={`${adType}AudienceReport`}
      >
        <SponsoredAdsAudienceReportTable
          state={state}
          initialState={initialState}
          setters={setters}
          columns={columns}
          grouping={grouping}
          onGroupingChange={onGroupingChange}
          adType={adType}
          setAdType={setAdType}
        />
      </MetricsCard>
    </PageContainer>
  );
}

function SponsoredAdsAudienceReport() {
  const { t } = useTranslation(["common"]);
  const hasOrgSummaryReportModule = useModuleEnabled("ORG_SUMMARY_REPORT");
  const [groupingParam, setGrouping] = useQueryParams("dims", "segment_name");
  const [adTypeParam, setAdType] = useQueryParams("adType", "sponsoredProduct");
  const parsedGrouping = sponsoredAdsAudienceGroupingSchema.safeParse(groupingParam);
  const grouping: SponsoredAdsAudienceGrouping = parsedGrouping.success ? parsedGrouping.data : "segment_name";
  const parsedAdType = sponsoredAdsAudienceAdTypeSchema.safeParse(adTypeParam);
  const adType: SponsoredAdsAudienceAdType = parsedAdType.success ? parsedAdType.data : "sponsoredProduct";

  const breadCrumbItems = [
    {
      name: t(($) => $.common.sidebar["Audience Report"]),
      href: "/user/report/audience",
      dropdownLinkOptions: REPORT_LINKS,
      searchable: false,
      dropdownSubtitle: t(($) => $.common.sidebar["Reports"]),
    },
  ];

  if (
    hasOrgSummaryReportModule &&
    !breadCrumbItems[0]?.dropdownLinkOptions.find((o) => o.name === ORG_SUMMARY_REPORT_BREADCRUMB_SPEC.name)
  ) {
    breadCrumbItems[0]?.dropdownLinkOptions.unshift(ORG_SUMMARY_REPORT_BREADCRUMB_SPEC);
  }

  return (
    <DatesProvider>
      <BreadcrumbDates items={breadCrumbItems} />
      <SponsoredAdsAudienceReportBody
        key={`${adType}-${grouping}`}
        grouping={grouping}
        onGroupingChange={setGrouping}
        adType={adType}
        setAdType={(next) => setAdType(next)}
      />
    </DatesProvider>
  );
}

export const Route = createFileRoute("/_authenticated/user/report/audience")({
  component: SponsoredAdsAudienceReport,
  staticData: { application: "Reports" },
});
